import React, { memo, useEffect, useState } from "react";
import arrayMove from 'array-move';
import { Tab, Tabs } from 'react-bootstrap';
import { connect } from 'react-redux';
import { fetchInstruments } from "../../api/instrument/actions";
import { fetchProfile, saveProfile } from '../../api/profile/actions';
import { fetchSongbookDetail, fetchSongbooks, modifySongbookOrder, setActiveSongbook, setSongbookOrderType } from '../../api/songbooks/actions';
import SongbookListItem from '../../components/songbook';
import SongbookCreate from "../../components/songbook-create";
import { SvgDragHandle, SvgPlusLargeRed, SvgSearchRed } from '../../components/svg';
import ReactPaginate from 'react-paginate';
import { Spinner } from '../../utils';
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

const DEFAULT_SONGBOOK_ORDER_TYPE = 'created'
const PRIVATE_SONGBOOK_ORDER_TYPE_PREFERENCE = 'privateSongbookOrderType'
const PUBLIC_SONGBOOK_ORDER_TYPE_PREFERENCE = 'publicSongbookOrderType'
const SHARED_SONGBOOK_ORDER_TYPE_PREFERENCE = 'sharedSongbookOrderType'

const getSongbookOrderPreferenceKey = (songbookType) => {
  if (songbookType === 'public') {
    return PUBLIC_SONGBOOK_ORDER_TYPE_PREFERENCE
  }
  if (songbookType === 'shared') {
    return SHARED_SONGBOOK_ORDER_TYPE_PREFERENCE
  }
  return PRIVATE_SONGBOOK_ORDER_TYPE_PREFERENCE
}

const getSongbookOrderStateKey = (songbookType) => {
  if (songbookType === 'public') {
    return 'publicOrderType'
  }
  if (songbookType === 'shared') {
    return 'sharedOrderType'
  }
  return 'privateOrderType'
}

const getSongbookSearchStateKey = (songbookType) => {
  if (songbookType === 'public') {
    return 'searchText'
  }
  if (songbookType === 'shared') {
    return 'searchTextShared'
  }
  return 'searchTextPriv'
}

const getSongbookCollectionStateKey = (songbookType) => {
  if (songbookType === 'public') {
    return 'publicSongbooks'
  }
  if (songbookType === 'shared') {
    return 'sharedSongbooks'
  }
  return 'privateSongbooks'
}

const getProfileSongbookOrderType = (profile, songbookType) => {
  if (!profile || !profile.hasFetched || !profile.ui_preferences) {
    return null
  }

  const key = getSongbookOrderPreferenceKey(songbookType)
  const orderType = profile.ui_preferences[key]
  const validOrderTypes = songbookType === 'public' || songbookType === 'shared'
    ? ['created', 'title']
    : ['custom', 'created', 'title']

  return validOrderTypes.indexOf(orderType) !== -1 ? orderType : DEFAULT_SONGBOOK_ORDER_TYPE
}

const getSongbookOrderType = (props, songbookType) => {
  const profileOrderType = getProfileSongbookOrderType(props.profile, songbookType)
  if (profileOrderType) {
    return profileOrderType
  }

  const storeKey = getSongbookOrderPreferenceKey(songbookType)

  return props.songbooks[storeKey] || DEFAULT_SONGBOOK_ORDER_TYPE
}

const SortableSongbookListItem = memo(function SortableSongbookListItem({ findItem, onSortEnd, onMoveToPosition, songbook, index, children }) {
  const id = songbook.id
  const originalIndex = findItem(id).index
  const [draftPosition, setDraftPosition] = useState(String(index + 1))

  useEffect(() => {
    setDraftPosition(String(index + 1))
  }, [index])

  const moveSongbook = (oldIndex, newIndex) => {
    onSortEnd({ oldIndex, newIndex })
  }

  const commitPosition = () => {
    if (draftPosition.trim() === "") {
      setDraftPosition(String(index + 1))
      return
    }

    const normalizedPosition = onMoveToPosition(id, draftPosition)
    if (normalizedPosition !== null && normalizedPosition !== undefined) {
      setDraftPosition(String(normalizedPosition))
    }
  }

  const [{ opacity }, drag] = useDrag(
    () => ({
      type: 'SongbookListItem',
      item: { id, originalIndex },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.4 : 1.0,
      })
    }),
    [id, originalIndex, moveSongbook],
  )

  const [{ backgroundColor }, drop] = useDrop(
    () => ({
      accept: 'SongbookListItem',
      drop({ id: draggedId }) {
        if (draggedId !== id) {
          const { index: overIndex } = findItem(id)
          moveSongbook(findItem(draggedId).index, overIndex)
        }
      },
      collect: (monitor) => ({
        backgroundColor: monitor.isOver() ? 'rgba(255, 255, 255, 0.3)' : '',
      })
    }),
    [findItem, moveSongbook],
  )

  return (
    <div ref={drop} className="songbook-sortable-wrapper" style={{ backgroundColor, opacity }}>
      <div className="songbook-order-controls">
        <button ref={drag} type="button" className="songbook-drag-handle" aria-label={gettext("Songbook order")}>
          <SvgDragHandle />
        </button>
        <input
          type="number"
          min="1"
          className="songbook-order-input"
          aria-label={gettext("Songbook order")}
          value={draftPosition}
          onChange={(e) => setDraftPosition(e.target.value)}
          onBlur={commitPosition}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitPosition()
            }
            if (e.key === 'Escape') {
              setDraftPosition(String(index + 1))
              e.currentTarget.blur()
            }
          }}
        />
      </div>
      {children}
    </div>
  )
})

class SongbookContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      publicSongbooks: this.props.songbooks.publicSongbooks,
      privateSongbooks: this.props.songbooks.privateSongbooks,
      sharedSongbooks: this.props.songbooks.sharedSongbooks,
      searchText: this.props.songbooks.searchText ? this.props.songbooks.searchText : '',
      searchTextPriv: this.props.songbooks.searchTextPriv ? this.props.songbooks.searchTextPriv : '',
      searchTextShared: this.props.songbooks.searchTextShared ? this.props.songbooks.searchTextShared : '',
      activeKey: this.props.songbooks.activeSongbook ? this.props.songbooks.activeSongbook : 'private',
      privateOrderType: getSongbookOrderType(this.props, 'private'),
      publicOrderType: getSongbookOrderType(this.props, 'public'),
      sharedOrderType: getSongbookOrderType(this.props, 'shared'),
      activePage: 1,
    }
    this.onSearchInputChange = this.onSearchInputChange.bind(this)
    this.findSongbook = this.findSongbook.bind(this)
    this.onSortEnd = this.onSortEnd.bind(this)
    this.onOrderTypeChange = this.onOrderTypeChange.bind(this)
    this.moveSongbookToPosition = this.moveSongbookToPosition.bind(this)
  }

  componentDidMount() {
    this.props.dispatch(fetchInstruments())
    if (this.props.user.isAuthenticated && !this.props.profile.hasFetched && !this.props.profile.isFetching) {
      this.props.dispatch(fetchProfile())
    }
    if (this.props.user.isAuthenticated && this.props.songbooks.privateSongbooks.results.length === 0) {
      this.props.dispatch(fetchSongbooks(null, false, this.state.searchTextPriv, null, this.state.privateOrderType))
    }
    if (this.props.user.isAuthenticated && this.props.songbooks.sharedSongbooks.results.length === 0) {
      this.props.dispatch(fetchSongbooks(null, false, this.state.searchTextShared, null, this.state.sharedOrderType, true))
    }
    if (this.props.songbooks.publicSongbooks.results.length === 0) {
      this.props.dispatch(fetchSongbooks(null, true, this.state.searchText, null, this.state.publicOrderType))
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.user.email !== prevProps.user.email) {
      if (this.props.user.isAuthenticated && !this.props.profile.hasFetched && !this.props.profile.isFetching) {
        this.props.dispatch(fetchProfile())
      }
      if (this.props.user.isAuthenticated) {
        Promise.resolve(this.props.dispatch(fetchSongbooks(null, false, this.state.searchTextPriv, null, this.state.privateOrderType)))
          .then(this.props.dispatch(fetchSongbooks(null, false, this.state.searchTextShared, null, this.state.sharedOrderType, true)))
          .then(this.props.dispatch(fetchSongbooks(null, true, this.state.searchText, null, this.state.publicOrderType)))
      } else {
        this.props.dispatch(fetchSongbooks(null, true, this.state.searchText, null, this.state.publicOrderType))
      }
    }
    if (JSON.stringify(this.props.songbooks.publicSongbooks) != JSON.stringify(prevProps.songbooks.publicSongbooks)) {
      this.setState({
        publicSongbooks: this.props.songbooks.publicSongbooks
      })
      if (this.props.songbooks.publicSongbooks.previous == null) {
        this.setState({
          activePage: 1
        })
      }
    }
    if (JSON.stringify(this.props.songbooks.privateSongbooks) != JSON.stringify(prevProps.songbooks.privateSongbooks)) {
      this.setState({
        privateSongbooks: this.props.songbooks.privateSongbooks
      })
      if (this.props.songbooks.privateSongbooks.previous == null) {
        this.setState({
          activePage: 1
        })
      }
    }
    if (JSON.stringify(this.props.songbooks.sharedSongbooks) != JSON.stringify(prevProps.songbooks.sharedSongbooks)) {
      this.setState({
        sharedSongbooks: this.props.songbooks.sharedSongbooks
      })
      if (this.props.songbooks.sharedSongbooks.previous == null) {
        this.setState({
          activePage: 1
        })
      }
    }
    if (this.props.songbooks.activeSongbook != prevProps.songbooks.activeSongbook) {
      this.setState({
        activeKey: this.props.songbooks.activeSongbook
      })
    }
    if (this.props.songbooks.privateSongbookOrderType != prevProps.songbooks.privateSongbookOrderType) {
      this.setState({
        privateOrderType: this.props.songbooks.privateSongbookOrderType
      })
    }
    if (this.props.songbooks.publicSongbookOrderType != prevProps.songbooks.publicSongbookOrderType) {
      this.setState({
        publicOrderType: this.props.songbooks.publicSongbookOrderType
      })
    }
    if (this.props.songbooks.sharedSongbookOrderType != prevProps.songbooks.sharedSongbookOrderType) {
      this.setState({
        sharedOrderType: this.props.songbooks.sharedSongbookOrderType
      })
    }
    if (this.props.profile.hasFetched && this.props.profile.ui_preferences !== prevProps.profile.ui_preferences) {
      const privateOrderType = getSongbookOrderType(this.props, 'private')
      const publicOrderType = getSongbookOrderType(this.props, 'public')
      const sharedOrderType = getSongbookOrderType(this.props, 'shared')
      const stateUpdate = {}

      if (privateOrderType !== this.state.privateOrderType) {
        stateUpdate.privateOrderType = privateOrderType
        this.props.dispatch(setSongbookOrderType('private', privateOrderType))
        if (this.props.user.isAuthenticated) {
          this.props.dispatch(fetchSongbooks(null, false, this.state.searchTextPriv, null, privateOrderType))
        }
      }
      if (publicOrderType !== this.state.publicOrderType) {
        stateUpdate.publicOrderType = publicOrderType
        this.props.dispatch(setSongbookOrderType('public', publicOrderType))
        this.props.dispatch(fetchSongbooks(null, true, this.state.searchText, null, publicOrderType))
      }
      if (sharedOrderType !== this.state.sharedOrderType) {
        stateUpdate.sharedOrderType = sharedOrderType
        this.props.dispatch(setSongbookOrderType('shared', sharedOrderType))
        if (this.props.user.isAuthenticated) {
          this.props.dispatch(fetchSongbooks(null, false, this.state.searchTextShared, null, sharedOrderType, true))
        }
      }
      if (Object.keys(stateUpdate).length > 0) {
        this.setState({
          ...stateUpdate,
          activePage: 1,
        })
      }
    }
  }

  onSearchInputChange(e) {
    e.preventDefault();
    const { value, id } = e.target;

    if (id == 'public') {
      this.setState({
        searchText: value
      })
      if (value.length >= 3) {
        this.props.dispatch(fetchSongbooks(null, true, value, null, this.state.publicOrderType))
      }
    } else if (id == 'private') {
      this.setState({
        searchTextPriv: value
      })
      if (value.length >= 3) {
        this.props.dispatch(fetchSongbooks(null, false, value, null, this.state.privateOrderType))
      }
    } else if (id == 'shared') {
      this.setState({
        searchTextShared: value
      })
      if (value.length >= 3) {
        this.props.dispatch(fetchSongbooks(null, false, value, null, this.state.sharedOrderType, true))
      }
    }
    
  }

  onOrderTypeChange(e) {
    const orderType = e.target.value
    const songbookType = e.target.dataset.songbookType || (e.target.id == 'public-songbooks-order' ? 'public' : this.state.activeKey)
    const isPublic = songbookType == 'public'
    const isShared = songbookType == 'shared'

    this.props.dispatch(setSongbookOrderType(songbookType, orderType))
    if (this.props.user.isAuthenticated) {
      this.props.dispatch(saveProfile(
        getSongbookOrderPreferenceKey(songbookType),
        orderType,
        true,
      ))
    }
    this.setState({
      activePage: 1,
      [getSongbookOrderStateKey(songbookType)]: orderType,
    })
    this.props.dispatch(fetchSongbooks(
      null,
      isPublic,
      this.state[getSongbookSearchStateKey(songbookType)],
      null,
      orderType,
      isShared,
    ))
  }

  handlePageChange(pageObj) {
    let pageNumber = pageObj.selected + 1;
    console.log(pageNumber);
    let page = ''
    if (pageNumber > this.state.activePage) {
      page = 'next'
    } else if (pageNumber < this.state.activePage) {
      page = 'previous'
    }
    const activeCollection = this.state[getSongbookCollectionStateKey(this.state.activeKey)]
    let songbook_url = activeCollection[page]
    let offset = songbook_url !== '' && (pageNumber-1) * 100

    let is_public = this.state.activeKey == 'public' ? true : false
    let is_shared = this.state.activeKey == 'shared' ? true : false

    this.props.dispatch(fetchSongbooks(null, is_public, this.state[getSongbookSearchStateKey(this.state.activeKey)], offset, this.state[getSongbookOrderStateKey(this.state.activeKey)], is_shared));
    this.setState({ activePage: pageNumber });
}

  findSongbook(id) {
    const songbook = this.state.privateSongbooks.results.filter((c) => c.id === id)[0]
    return {
      songbook,
      index: this.state.privateSongbooks.results.indexOf(songbook),
    }
  }

  updateSongbookOrder(newResults) {
    const newPrivateSongbooks = {
      ...this.state.privateSongbooks,
      results: newResults,
    }
    const start = (this.state.activePage - 1) * 100

    this.setState({
      privateSongbooks: newPrivateSongbooks,
    })
    this.props.dispatch(modifySongbookOrder(newResults.map((item) => item.id), start))
  }

  onSortEnd({ oldIndex, newIndex }) {
    if (oldIndex === newIndex || oldIndex < 0 || newIndex < 0) {
      return;
    }

    this.updateSongbookOrder(arrayMove(this.state.privateSongbooks.results, oldIndex, newIndex))
  }

  moveSongbookToPosition(songbookId, requestedPosition) {
    const oldIndex = this.state.privateSongbooks.results.findIndex((item) => item.id === songbookId)
    if (oldIndex === -1) {
      return null
    }

    const parsedPosition = parseInt(requestedPosition, 10)
    if (Number.isNaN(parsedPosition)) {
      return oldIndex + 1
    }

    const newIndex = Math.min(Math.max(parsedPosition - 1, 0), this.state.privateSongbooks.results.length - 1)
    if (oldIndex === newIndex) {
      return newIndex + 1
    }

    this.updateSongbookOrder(arrayMove(this.state.privateSongbooks.results, oldIndex, newIndex))
    return newIndex + 1
  }

  render() {  
  
    const { songbooks, songbook } = this.props
    let activeKey = this.state.activeKey
    const hasSharedSongbooks = this.state.sharedSongbooks.results &&
      (this.state.sharedSongbooks.results.length > 0 || this.state.sharedSongbooks.count > 0)
    if (activeKey == 'shared' && !hasSharedSongbooks) {
      activeKey = 'private'
    }
    const activeCollectionStateKey = getSongbookCollectionStateKey(activeKey)
    const activeSearchStateKey = getSongbookSearchStateKey(activeKey)
    const activeOrderStateKey = getSongbookOrderStateKey(activeKey)
    const activeSongbooks = this.state[activeCollectionStateKey]
    let itemsCount = activeSongbooks.count
    const activeOrderType = this.state[activeOrderStateKey]
    const canReorderPrivateSongbooks = activeKey == 'private' &&
      activeOrderType === 'custom' &&
      this.state.searchTextPriv.trim() === '' &&
      this.state.privateSongbooks.results &&
      this.state.privateSongbooks.results.length > 1 &&
      this.state.privateSongbooks.results.every((item) => item.owner === this.props.user.username)
    return (
      <div className="container-fluid ">
        <div className="row justify-content-center">
          <div className="col-16 col-sm-14 col-md-12 col-lg-8">
            <h1>{gettext('Songbooks')}
              {songbooks.isFetching === true &&
              <div className="text-align-center spinner-container">
                <Spinner inline/>
              </div>
              }
              {this.props.user.isAuthenticated &&
                <div className="svg-plus-container"><SvgPlusLargeRed/></div>
              }
            </h1>


            {this.props.user.isAuthenticated &&
              <SongbookCreate />
            }
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-16 col-sm-14 col-md-12 col-lg-8">
            {this.props.user.isAuthenticated ?
              <>
                <div className="songbooks-toolbar">
                  <div className="songbooks-search-container">
                    <input type="text" className="private search-input dark-bg" placeholder={gettext('Search')} id={activeKey} onChange={this.onSearchInputChange} value={this.state[activeSearchStateKey]}></input>
                    <SvgSearchRed />
                  </div>
                  <div className="songbooks-order-container">
                    <label htmlFor="songbooks-order" className="sr-only">{gettext('Songbook order')}</label>
                    <select id="songbooks-order" className="songbooks-order-selector" value={activeOrderType} onChange={this.onOrderTypeChange} data-songbook-type={activeKey}>
                      {activeKey == 'private' &&
                        <option value="custom">{gettext('Custom order')}</option>
                      }
                      <option value="created">{gettext('Newest first')}</option>
                      <option value="title">{gettext('Title A-Z')}</option>
                    </select>
                  </div>
                </div>
                <Tabs activeKey={activeKey} id="uncontrolled-tab-example" onSelect={(key) => this.props.dispatch(setActiveSongbook(key))}>
                  <Tab eventKey="private" title={gettext("Private list")}>
                    {this.state.privateSongbooks.results && Object.keys(this.state.privateSongbooks.results).length > 0 &&
                      <>
                        {canReorderPrivateSongbooks ?
                          <DndProvider backend={HTML5Backend}>
                            {this.state.privateSongbooks.results.map((item, index) => {
                              return <SortableSongbookListItem
                                findItem={this.findSongbook}
                                onSortEnd={this.onSortEnd}
                                onMoveToPosition={this.moveSongbookToPosition}
                                songbook={item}
                                key={"songbook-private-wrapper_" + item.id}
                                index={index}>
                                <SongbookListItem songbooks={songbooks} isFetching={songbook.isFetching} songbook={item} key={index + "_songbook-private_" + item.id} index={index} user={this.props.user} getSongbook={(id) => this.props.dispatch(fetchSongbookDetail(id, this.state.page))} onlyPublic={false} dispatch={this.props.dispatch} />
                              </SortableSongbookListItem>
                            })}
                          </DndProvider>
                          :
                          this.state.privateSongbooks.results.map((item, index) => {
                            return <SongbookListItem songbooks={songbooks} isFetching={songbook.isFetching} songbook={item} key={index + "_songbook-private_" + item.id} index={index} user={this.props.user} getSongbook={(id) => this.props.dispatch(fetchSongbookDetail(id, this.state.page))} onlyPublic={false} dispatch={this.props.dispatch} />
                          })
                        }
                      </>
                    }
                  </Tab>
                  {hasSharedSongbooks &&
                    <Tab eventKey="shared" title={gettext("Shared with me")}>
                      {this.state.sharedSongbooks.results && Object.keys(this.state.sharedSongbooks.results).length > 0 &&
                        this.state.sharedSongbooks.results.map((item, index) => {
                          return <SongbookListItem songbooks={songbooks} isFetching={songbook.isFetching} songbook={item} key={index + "_songbook-shared_" + item.id} index={index} user={this.props.user} getSongbook={(id) => this.props.dispatch(fetchSongbookDetail(id, false))} onlyPublic={false} dispatch={this.props.dispatch} />
                        })}
                    </Tab>
                  }
                  <Tab eventKey="public" title={gettext("Public list")}>
                    {this.state.publicSongbooks.results && Object.keys(this.state.publicSongbooks.results).length > 0 &&
                      this.state.publicSongbooks.results.map((item, index) => {
                        return <SongbookListItem songbooks={songbooks} isFetching={songbook.isFetching} songbook={item} key={index + "_songbook-public_" + item.id} index={index} user={this.props.user} getSongbook={(id) => this.props.dispatch(fetchSongbookDetail(id, this.state.page))} onlyPublic={true} dispatch={this.props.dispatch} />
                      })}
                  </Tab>
                </Tabs>
              </> : this.state.publicSongbooks.results && Object.keys(this.state.publicSongbooks.results).length > 0 &&
              <>
                <div className="songbooks-toolbar anonymous">
                  <div className="songbooks-search-container">
                    <input type="text" className="public search-input dark-bg" id="public" placeholder={gettext('Search')} onChange={this.onSearchInputChange} value={this.state.searchText}></input>
                    <SvgSearchRed />
                  </div>
                  <div className="songbooks-order-container">
                    <label htmlFor="public-songbooks-order" className="sr-only">{gettext('Songbook order')}</label>
                    <select id="public-songbooks-order" className="songbooks-order-selector" value={this.state.publicOrderType} onChange={this.onOrderTypeChange}>
                      <option value="created">{gettext('Newest first')}</option>
                      <option value="title">{gettext('Title A-Z')}</option>
                    </select>
                  </div>
                </div>
                {this.state.publicSongbooks.results.map((item, index) => {
                  return <SongbookListItem songbooks={songbooks} isFetching={songbook.isFetching} songbook={item} key={index + "_songbook_" + item.id} index={index} user={this.props.user} getSongbook={(id) => this.props.dispatch(fetchSongbookDetail(id, this.state.page))} onlyPublic={true} dispatch={this.props.dispatch} />
                })}
              </>
            }
            {!songbooks.isFetching && activeSongbooks.count == 0 &&
              <div>{gettext('No songbooks meet the criteria.')}</div>}
            {itemsCount > 100 && this.props.songbooks.isFetching && <div style={{marginLeft:'50%'}}><Spinner inline /></div>}
            {itemsCount > 100 && <div className="pagination-react">
              {<ReactPaginate
                    className="pagination"
                    disabledClassName="disabled"
                    activeClassName="active"
                    pageCount={Math.ceil(itemsCount/100)}
                    pageRangeDisplayed={3}
                    onPageChange={this.handlePageChange.bind(this)}
                    previousLabel="⟨"
                    nextLabel="⟩"
                />}
                </div>}
          </div>
        </div>
      </div>
    )

  }
}

SongbookContainer.defaultProps = {
}

function mapStateToProps(state) {

  var f = {
    songbooks: {},
    songbook: {},
    profile: {},
    user: {},
  }
  f.songbooks = (state.songbooks === undefined) ? {} : state.songbooks
  f.songbook = (state.songbook === undefined) ? {} : state.songbook
  f.profile = (state.profile === undefined) ? {} : state.profile
  f.user = (state.user === undefined) ? {} : state.user
  return f;
}

export default connect(mapStateToProps)(SongbookContainer);
